import { useInterval } from '@elotech/components';
import { alert } from 'common/components';
import { useState } from 'react';
import { ArquivoService, ProcessoArquivoZipService } from 'service';
import { PollingDelay } from 'types';

const TITULO_FALHA = 'Falha ao gerar ZIP';
const MSG_FALHA_AO_INICIAR =
  'Não foi possível iniciar a geração do ZIP dos anexos. Tente novamente.';

const formatZipDownloadLabel = (
  isGerandoZip: boolean,
  progresso: string | null
) => {
  if (!isGerandoZip) {
    return 'Download Completo dos Anexos';
  }

  return progresso ? `Gerando ZIP... (${progresso})` : 'Gerando ZIP...';
};

export const useProcessoZipDownload = (
  zipQueryParams: string | null | undefined,
  { wrapLoading }: { wrapLoading: (promise: Promise<any>) => void }
) => {
  const [situacao, setSituacao] = useState<string | null>(null);
  const [progresso, setProgresso] = useState<string | null>(null);
  const [pollingAtivo, setPollingAtivo] = useState(false);
  const [pollingDelay, setPollingDelay] = useState(PollingDelay.DEFAULT);

  const isGerandoZip = pollingAtivo;

  const resetarProgresso = () => {
    setSituacao(null);
    setProgresso(null);
  };

  const onFalha = (mensagem: string) => {
    setPollingAtivo(false);
    resetarProgresso();
    alert({ type: 'error', title: TITULO_FALHA, text: mensagem });
  };

  useInterval(() => {
    if (!pollingAtivo || !zipQueryParams) {
      return;
    }

    ProcessoArquivoZipService.consultarSituacaoZip(zipQueryParams)
      .then(({ data }) => {
        if (data.erro) {
          onFalha(data.erro);
          return;
        }

        setSituacao(data.situacao);
        setProgresso(data.progresso ?? null);
        setPollingDelay(PollingDelay.DEFAULT);

        if (data.situacao === 'PROCESSADO') {
          setPollingAtivo(false);
          ArquivoService.downloadZipAnexos(data);
        }
      })
      .catch(() => setPollingDelay(PollingDelay.ON_ERROR));
  }, pollingDelay);

  const downloadArquivoSeparado = async processo => {
    await Promise.all(
      processo.arquivos.map(arquivo => ArquivoService.download(arquivo))
    );
  };

  const downloadAnexosInZip = processo => {
    if (!zipQueryParams) {
      return undefined;
    }

    const promise = ProcessoArquivoZipService.gerarZip(
      { arquivos: processo.arquivos },
      zipQueryParams
    )
      .then(({ data }) => {
        if (data.erro) {
          onFalha(data.erro);
          return;
        }

        setSituacao(data.situacao);
        setProgresso(data.progresso ?? null);
        setPollingAtivo(true);
        setPollingDelay(PollingDelay.DEFAULT);
      })
      .catch(() => onFalha(MSG_FALHA_AO_INICIAR));

    wrapLoading?.(promise);

    return promise;
  };

  const onRealizarDownloadDeTodosAnexos = processo => {
    alert({
      type: 'question',
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      title: 'Deseja que os Anexos sejam Zipados?',
      onConfirm: () => downloadAnexosInZip(processo),
      onCancel: () => downloadArquivoSeparado(processo)
    });
  };

  return {
    situacao,
    isGerandoZip,
    progresso,
    podeGerarZip: !!zipQueryParams,
    label: formatZipDownloadLabel(isGerandoZip, progresso),
    onRealizarDownloadDeTodosAnexos
  };
};
